import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useDefaultCRUDHandlers from "../../../../hooks/useDefaultCRUDHandlers";
import { ILead } from "../../../../models/Lead";
import useLeads from "../../../../data/useLeads";
import ContactFromId from "../../../contacts/ContactFromId";
import formatDateTime from "../../../../utils/formatDateTime";
import { DateTime } from "luxon";
import Modal from "../../../../layouts/Modal";

export default function LeadsTableCallCount({ lead }: { lead: ILead }) {
  const { t } = useTranslation("leads");
  const { updateCallCount, addCallNote } = useLeads(true, "LeadsTableCallCount");
  const { saveHandlers } = useDefaultCRUDHandlers("Leads");
  const [currentCallCount, setCurrentCallCount] = useState<number>(lead.callCount);
  const [callCountUpdatedOn, setCallCountUpdatedOn] = useState<string | null>(lead.callCountUpdatedOn);
  const [callCountUpdatedBy, setCallCountUpdatedBy] = useState<string | null>(lead.callCountUpdatedBy);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingDelta, setPendingDelta] = useState<number | null>(null);
  const [noteMessage, setNoteMessage] = useState("");

  useEffect(() => {
    setCurrentCallCount(lead.callCount);
    setCallCountUpdatedOn(lead.callCountUpdatedOn);
    setCallCountUpdatedBy(lead.callCountUpdatedBy);
  }, [lead.callCount, lead.callCountUpdatedOn, lead.callCountUpdatedBy]);

  const updateLeadCallCount = (delta: number) => {
    updateCallCount.mutate({ leadId: lead.id, delta }, {
      onSuccess: (response) => {
        const updatedLead = response.data;
        setCurrentCallCount(updatedLead.callCount);
        setCallCountUpdatedOn(updatedLead.callCountUpdatedOn);
        setCallCountUpdatedBy(updatedLead.callCountUpdatedBy);
        setIsModalOpen(false);
        setPendingDelta(null);
        setNoteMessage("");
      },
      onError: saveHandlers.onError
    });
  };

  const handleCallListClick = (e: any, delta: number) => {
    e.preventDefault();
    e.stopPropagation();

    const callCount = currentCallCount + delta;

    //Don't want it under 0 or over 10
    if (callCount < -1 || callCount > 10) {
      return;
    }

    if (delta > 0){
      setPendingDelta(delta);
      setNoteMessage("");
      setIsModalOpen(true);
    } else {
      updateLeadCallCount(delta);
    }    
  }

  const handleSkipNote = () => {
    if (pendingDelta === null) return;

    updateLeadCallCount(pendingDelta);
  };

  const handleSaveNote = () => {
    if (pendingDelta === null || !noteMessage.trim()) return;

    addCallNote.mutate({ leadId: lead.id, message: noteMessage.trim(), date: DateTime.now().toISO() ?? undefined, callCount: nextCallNumber }, {
      onSuccess: () => {
        updateLeadCallCount(pendingDelta);
      },
      onError: saveHandlers.onError
    });
  };

  const nextCallNumber = currentCallCount + (pendingDelta ?? 0);
  const isSaving = updateCallCount.isLoading || addCallNote.isLoading;

  return (
    <>
      <Modal open={isModalOpen} width={"max-w-xl"} setOpen={setIsModalOpen}>
        <div>
          <h3 className="text-lg font-medium text-gray-900">{t("Notes for call {{count}}", { count: nextCallNumber })}</h3>

          <div className="mt-4">
            <label className="text-sm font-medium text-gray-500">{t("Call Note")}</label>
            <textarea
              value={noteMessage}
              onChange={(e) => setNoteMessage(e.target.value)}
              rows={4}
              className="w-full mt-1 text-gray-900 border-1 border-gray-300 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 px-3 py-2 rounded-md"
              placeholder={t("Add optional call note")}
            />
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <button
              type="button"
              onClick={handleSkipNote}
              disabled={isSaving}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              {t("No notes / Skip")}
            </button>
            <button
              type="button"
              onClick={handleSaveNote}
              disabled={isSaving || !noteMessage.trim()}
              className="rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
            >
              {t("Save note")}
            </button>
          </div>
        </div>
      </Modal>
      <div className="flex items-center">

        <span className="flex flex-col items-center justify-center text-center w-24">
          <span className="">
            {currentCallCount === 10 ? (t("Customer to call back")) :
              (
                currentCallCount === 0 ? (t("Awaiting Call")) :
                  currentCallCount === -1 ? (t("Avoiding")) :
                    (t("Called {{count}}", { count: currentCallCount }))
              )}
          </span>
          {(currentCallCount > 0 || currentCallCount === -1) && (
            <>
              <span className="text-xs">{callCountUpdatedOn && formatDateTime(callCountUpdatedOn, DateTime.DATETIME_SHORT)}</span>
              <span className="text-xs">{callCountUpdatedBy && <ContactFromId contactId={callCountUpdatedBy} />}</span>
            </>
          )}
        </span>

        <div className="flex flex-col ml-2">
          <button
            type="button"
            className="flex items-center justify-center w-6 h-6 text-lg font-bold rounded-full hover:bg-gray-200"
            onClick={(e) => handleCallListClick(e, 1)}>+</button>
          <button
            type="button"
            className="flex items-center justify-center w-6 h-6 text-lg font-bold rounded-full hover:bg-gray-200"
            onClick={(e) => handleCallListClick(e, -1)}>-</button>
        </div>
      </div>
    </>
  )
}
